import { SelectOption } from '@globaleris/ui.library'
import {
  apiDownloadConsumo,
  apiDownloadFiles,
  apiDownloadInvoiceDocument,
  apiDownloadLeadPriceTemplate,
  apiDownloadMeasureTemplate,
  apiDownloadPriceListByQuote,
  apiDownloadProfileSelectionTemplate,
  apiDownloadProfileTemplate,
  apiFilesUpload,
  EnumCommodity,
  FilesLoadingResponse
} from '../../api'
import {
  apiDeleteDocument,
  apiFecthDocumentTypeByDocumentGorup,
  apiFecthDocumentTypeByDocumentGroupList,
  apiFetchDocumentObjectionsDescriptions,
  apiFetchDocumentsByContractId,
  apiFetchDocumentsByProcedureId,
  apiFetchDocumentsByTradeId,
  apiFetchDownloadDocument,
  apiFetchObjectionsByDocumentId,
  apiGetDocumentsByDocumentGroupList,
  apiGetDocumentsByLeadId,
  apiGetDocumentsByProfileId,
  apiGetDocumentsByType,
  apiPatchDocument,
  apiPostDocument
} from '../../api/Argentia/endpoints/documents/documents.api'
import { AtlasDocumentModel } from '../../api/Argentia/endpoints/documents/model/atlasDocument'
import {
  DocumentModel,
  DocumentPatchRequest,
  DocumentPostRequest
} from '../../api/Argentia/endpoints/documents/model/document'
import { EnumNivelDesglose } from '../../api/Argentia/enums/enumNivelDesglose'
import { EnumTipoDocumento } from '../../api/oData'
import {
  apiDeleteFilesLoadingDocument,
  apiGetTemplates
} from '../../api/Pioneer/endpoints/files/files.api'
import {
  ConsumoRequestModel,
  DownloadFileRequestModel,
  FilesUploadModel,
  TemplateFileModel
} from '../../api/Pioneer/endpoints/files/models/files'
import { EnumFileExtension } from '../../api/Pioneer/enums/enumFileExtension'
import { EnumFileType } from '../../api/Pioneer/enums/enumFileType'
import { DeleteFileLoadingResponse } from '../../api/Pioneer/model/Files'
import { enumKeyToValue } from '../../helpers'
import { mapFileStreamToFile } from '../../helpers/fileStreamToFile'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import { ServiceResponse } from '../../model/serviceResponse'
import { ObjectionTableModel } from '../invoicesPage/invoicesPage.Model'
import { mapDocumentToDocumentModelArray } from './documents.mapper'
import { EnumDocumentsNavigationFrom } from './documents.model'

export const fetchDocumentsByElementId = async (
  elementId: number,
  element: EnumDocumentsNavigationFrom
): Promise<ServiceResponse<DocumentModel[] | undefined>> => {
  return await (
    element === EnumDocumentsNavigationFrom.Contracts
      ? apiFetchDocumentsByContractId(elementId)
      : element === EnumDocumentsNavigationFrom.Procedures
        ? apiFetchDocumentsByProcedureId(elementId)
        : apiFetchDocumentsByTradeId(elementId)
  )
    .then(response => {
      return createSuccessServiceResponse(mapDocumentToDocumentModelArray(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchDocumentObjectionsDescriptions = async (): Promise<
  ServiceResponse<ObjectionTableModel[] | undefined>
> => {
  return await apiFetchDocumentObjectionsDescriptions()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchObjectionsByDocumentId = async (
  documentID: number
): Promise<ServiceResponse<ObjectionTableModel[] | undefined>> => {
  return await apiFetchObjectionsByDocumentId(documentID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchDocumentsTypesByDocumentGroup = async (
  documentGroupId: number
): Promise<ServiceResponse<SelectOption[] | undefined>> => {
  return await apiFecthDocumentTypeByDocumentGorup(documentGroupId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchDocumentsTypesByDocumentGroupList = async (
  documentGroupList: number[]
): Promise<ServiceResponse<SelectOption[] | undefined>> => {
  return await apiFecthDocumentTypeByDocumentGroupList(documentGroupList)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createDocument = async (
  request: DocumentPostRequest
): Promise<ServiceResponse<DocumentModel | undefined>> => {
  return await apiPostDocument(request)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateDocument = async (
  request: DocumentPatchRequest
): Promise<ServiceResponse<DocumentModel | undefined>> => {
  return await apiPatchDocument(request)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deleteDocument = async (documentId: number) => {
  return await apiDeleteDocument(documentId)
    .then(res => {
      return createSuccessServiceResponse(res)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getDocumentByType = async (
  typeID: number[]
): Promise<ServiceResponse<DocumentModel[] | undefined>> => {
  return await apiGetDocumentsByType(typeID)
    .then(response => {
      return createSuccessServiceResponse(mapDocumentToDocumentModelArray(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

// reemplaza fetchDocumentsTypesByDocumentGroupList + getDocumentByType para Atlas: un solo llamado por documentGroupId.
// el back devuelve `type` como nombre del enum (ej. "UrlAtlasESP"), aquí se convierte al valor numérico de EnumTipoDocumento
// tolera que el back mande el nombre del enum ("UrlAtlasESP") o ya el valor numérico (201);
// enumKeyToValue con un número hace el mapeo inverso (número -> nombre) y rompería el filtro
const toAtlasDocumentType = (type: string | number): EnumTipoDocumento =>
  typeof type === 'number' ? type : enumKeyToValue(EnumTipoDocumento, type)

export const getDocumentsByDocumentGroupList = async (
  documentGroupList: number[]
): Promise<ServiceResponse<AtlasDocumentModel[] | undefined>> => {
  return await apiGetDocumentsByDocumentGroupList(documentGroupList)
    .then(response => {
      return createSuccessServiceResponse(
        response.map(item => ({
          ...item,
          type: toAtlasDocumentType(item.type)
        }))
      )
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getDocumentsByProfileId = async (
  profileId: number
): Promise<ServiceResponse<DocumentModel[] | undefined>> => {
  return await apiGetDocumentsByProfileId(profileId)
    .then(response => {
      return createSuccessServiceResponse(mapDocumentToDocumentModelArray(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getDocumentsByLeadId = async (
  leadId: number
): Promise<ServiceResponse<DocumentModel[] | undefined>> => {
  return await apiGetDocumentsByLeadId(leadId)
    .then(response => {
      return createSuccessServiceResponse(mapDocumentToDocumentModelArray(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const downloadDocument = async (
  elementId: number
): Promise<ServiceResponse<File | undefined>> => {
  return await apiFetchDownloadDocument(elementId)
    .then(response => {
      return createSuccessServiceResponse(mapFileStreamToFile(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

// borra archivo (Storage) + fila (G_DOCUMENTO) en un solo paso; no aplica a documentos sin archivo (ej. urls)
export const deleteDocumentFile = async (
  documentId: number
): Promise<ServiceResponse<DeleteFileLoadingResponse | undefined>> => {
  return await apiDeleteFilesLoadingDocument(documentId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const downloadProfileTemplate = async (
  profileId: number,
  isSeasonsTemplate: boolean
): Promise<ServiceResponse<File | undefined>> => {
  return await apiDownloadProfileTemplate(profileId, isSeasonsTemplate)
    .then(response => {
      return createSuccessServiceResponse(mapFileStreamToFile(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}
export const downloadLeadPriceTemplate = async (
  commodity: EnumCommodity
): Promise<ServiceResponse<File | undefined>> => {
  return await apiDownloadLeadPriceTemplate(commodity)
    .then(response => {
      return createSuccessServiceResponse(mapFileStreamToFile(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}
export const downloadLeadProfileTemplate = async (
  leadID: number,
  breakdownLevel: EnumNivelDesglose | undefined
): Promise<ServiceResponse<File | undefined>> => {
  return await apiDownloadMeasureTemplate(leadID, breakdownLevel)
    .then(response => {
      return createSuccessServiceResponse(mapFileStreamToFile(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const downloadProfileSelectionTemplate = async (
  leadID: number
): Promise<ServiceResponse<File | undefined>> => {
  return await apiDownloadProfileSelectionTemplate(leadID)
    .then(response => {
      return createSuccessServiceResponse(mapFileStreamToFile(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const uploadFile = async (
  request: FilesUploadModel
): Promise<ServiceResponse<FilesLoadingResponse[] | undefined>> => {
  return await apiFilesUpload(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const downloadFiles = async (
  request: DownloadFileRequestModel
): Promise<ServiceResponse<File | undefined>> => {
  return await apiDownloadFiles(request)
    .then(response => {
      return createSuccessServiceResponse(mapFileStreamToFile(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const downloadInvoiceDocument = async (
  invoiceID: number,
  fileExtension: EnumFileExtension
): Promise<ServiceResponse<File | undefined>> => {
  return await apiDownloadInvoiceDocument(invoiceID, fileExtension)
    .then(response => {
      return createSuccessServiceResponse(mapFileStreamToFile(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const downloadCurrentBADFile = async (
  request: ConsumoRequestModel
): Promise<ServiceResponse<File | undefined>> => {
  return await apiDownloadConsumo(request)
    .then(response => {
      return createSuccessServiceResponse(mapFileStreamToFile(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const downloadPriceListByQuote = async (
  idOferta: number
): Promise<ServiceResponse<File | undefined>> => {
  return await apiDownloadPriceListByQuote(idOferta)
    .then(response => {
      return createSuccessServiceResponse(mapFileStreamToFile(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchTemplates = async (): Promise<
  ServiceResponse<TemplateFileModel[] | undefined>
> => {
  return await apiGetTemplates()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const downloadTemplate = async (
  fileName: string
): Promise<ServiceResponse<File | undefined>> => {
  return await apiDownloadFiles({ blobFiles: [{ documentType: EnumFileType.Template, fileName }] })
    .then(response => {
      return createSuccessServiceResponse(mapFileStreamToFile(response))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}
